import { DataGrid, type GridColumn } from "@/components/data-grid";
import { EmptyState } from "@/components/EmptyState";
import { KpisIcon } from "@/components/icons";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { useQuery } from "@tanstack/react-query";

import { errorMessage } from "../lib/query.ts";

import { kpisQuery, type KpiSummary } from "./kpis.ts";

/** Fuera del componente: dentro cambiarían de identidad en cada render. */
const KPI_COLUMNS: readonly GridColumn<KpiSummary>[] = [
  {
    key: "code",
    label: "Código",
    render: (kpi) => <span className="font-mono">{kpi.code}</span>,
  },
  { key: "name", label: "Nombre", render: (kpi) => kpi.name },
  {
    key: "createdAt",
    label: "Dado de alta",
    render: (kpi) => new Date(kpi.createdAt).toLocaleDateString("es"),
    sortValue: (kpi) => kpi.createdAt,
  },
];

/**
 * Los KPIs: qué se está midiendo.
 *
 * No dependen del mes --un KPI se define una vez y se usa en los periodos que
 * haga falta (§7.3.2)--, así que esta sección no mira el selector de arriba.
 */
export function KpisPanel() {
  const list = useQuery(kpisQuery());
  const rows = list.data?.kpis ?? [];

  return (
    <section className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-4">
      {/* El nombre de la sección lo pone la cabecera de arriba. */}
      <header>
        <p className="text-sm text-muted-foreground">
          {list.isPending
            ? "Cargando…"
            : rows.length === 1
              ? "1 KPI definido"
              : `${String(rows.length)} KPIs definidos`}
        </p>
      </header>

      {list.error != null && (
        <Alert variant="destructive">
          <AlertTitle>No se pudo cargar</AlertTitle>
          <AlertDescription>{errorMessage(list.error)}</AlertDescription>
        </Alert>
      )}

      <DataGrid
        columns={KPI_COLUMNS}
        rows={rows}
        empty={
          <EmptyState icon={KpisIcon}>
            Todavía no hay ningún KPI. Se definen hablando con el agente.
          </EmptyState>
        }
      />
    </section>
  );
}
